import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CreditCard, Check, AlertTriangle, RefreshCw, Ban, RotateCcw, ChevronLeft, ChevronRight, Undo2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { t, formatMoney, formatDate } from '../i18n';
import { monthKey, addMonthsToKey } from '../lib/practiceTime';
import {
  Card, Button, Input, Select, Field, Badge, Alert, Spinner, EmptyState, Modal, Table,
} from '../components/ui';

/**
 * What each professional owes the practice for using the platform — distinct
 * from what patients pay for sessions.
 *
 * The fee is uniform across professionals but differs by discipline, so pricing
 * is held per discipline and each professional bills on their primary one.
 * Overdue is derived, not stored: a period is overdue once it has ended and is
 * still unpaid, which stays true without a nightly job. "Ended" is on the
 * practice's calendar (0067), and so is the month shown.
 *
 * Blocking is manual by design. auto_block_on_overdue exists in settings and is
 * off; nothing acts on it yet.
 */

const SubscriptionsView = () => {
  // A YYYY-MM month on the practice's clock (lib/practiceTime.js).
  const [month, setMonth] = useState(() => monthKey());
  const [rows, setRows] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [paying, setPaying] = useState(null);
  const [method, setMethod] = useState('transfer');
  const [showPricing, setShowPricing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [statusResult, planResult] = await Promise.all([
      supabase
        .from('subscription_status')
        .select('*')
        .eq('period_start', `${month}-01`)
        .order('last_name'),
      supabase
        .from('subscription_plans')
        .select('id, discipline_id, monthly_amount, annual_amount, is_active, disciplines(name)')
        .order('discipline_id'),
    ]);
    if (statusResult.error) setError(statusResult.error.message);
    setRows(statusResult.data ?? []);
    setPlans(planResult.data ?? []);
    setLoading(false);
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusyId('generate');
    const { data, error: genError } = await supabase.rpc('ensure_subscription_periods', {
      p_period_start: `${month}-01`,
      p_billing_period: 'monthly',
    });
    setBusyId(null);
    if (genError) { setError(genError.message); return; }
    setNotice(t('subscriptions.generated', { created: data.created, updated: data.updated }));
    load();
  };

  const markPaid = async () => {
    setBusyId(paying.id);
    const { error: payError } = await supabase
      .from('therapist_subscriptions')
      .update({
        status: 'paid',
        paid_at: new Date().toISOString(),
        payment_method: method,
      })
      .eq('id', paying.id);
    setBusyId(null);
    setPaying(null);
    if (payError) { setError(payError.message); return; }
    load();
  };

  const reopen = async (row) => {
    if (!window.confirm(t('subscriptions.confirmReopen', { name: row.first_name + ' ' + row.last_name }))) return;
    setBusyId(row.id);
    const { error: reopenError } = await supabase.rpc('reopen_subscription_period', { p_id: row.id });
    setBusyId(null);
    if (reopenError) { setError(reopenError.message); return; }
    load();
  };

  const toggleBlock = async (row) => {
    const blocking = !row.is_blocked;
    if (!window.confirm(t(blocking ? 'professionals.confirmBlock' : 'professionals.confirmUnblock',
      { name: `${row.first_name} ${row.last_name}` }))) return;
    await supabase.from('therapists')
      .update({
        is_blocked: blocking,
        blocked_at: blocking ? new Date().toISOString() : null,
        blocked_reason: blocking ? t('subscriptions.blockedForNonPayment') : null,
      })
      .eq('id', row.therapist_id);
    load();
  };

  const savePricing = async (plan, patch) => {
    const { error: saveError } = await supabase
      .from('subscription_plans')
      .update(patch)
      .eq('id', plan.id);
    if (saveError) { setError(saveError.message); return; }
    load();
  };

  // A professional with no discipline has no fee to look up, so their period
  // is $0 — a configuration gap, not a real zero. Surfaced explicitly because
  // marking it paid moves no totals and otherwise looks like a broken button.
  const missingDiscipline = useMemo(
    () => rows.filter((row) => !row.discipline),
    [rows]
  );

  const totals = useMemo(() => rows.reduce((acc, row) => {
    if (row.status === 'paid') acc.collected += Number(row.amount ?? 0);
    else acc.outstanding += Number(row.amount ?? 0);
    if (row.is_overdue) acc.overdue += 1;
    return acc;
  }, { collected: 0, outstanding: 0, overdue: 0 }), [rows]);

  const shiftMonth = (offset) => setMonth((current) => addMonthsToKey(current, offset));

  if (loading) return <Spinner />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('subscriptions.title')}</h1>
          <p className="text-sm text-slate-500">{t('subscriptions.subtitle')}</p>
        </div>
        <Button variant="secondary" onClick={() => setShowPricing(true)}>
          <CreditCard size={16} />{t('subscriptions.feeByDiscipline')}
        </Button>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}
      {missingDiscipline.length > 0 && (
        <Alert tone="warning" title={t('subscriptions.missingDisciplineTitle')}>
          {t('subscriptions.missingDisciplineBody', { count: missingDiscipline.length })}{' '}
          <Link to="/profesionales" className="font-semibold underline">
            {t('subscriptions.assignDiscipline')}
          </Link>
        </Alert>
      )}

      {totals.overdue > 0 && (
        <Alert tone="warning" title={t('subscriptions.overdueTitle')}>
          {t('subscriptions.overdueAlert', { count: totals.overdue })}{' '}
          {t('subscriptions.manualBlockNote')}
        </Alert>
      )}

      <div className="flex items-center justify-between gap-3">
        <Button variant="secondary" onClick={() => shiftMonth(-1)}><ChevronLeft size={16} /></Button>
        <p className="text-sm font-semibold text-slate-800 first-letter:uppercase">
          {formatDate(`${month}-01`, { month: 'long', year: 'numeric' })}
        </p>
        <Button variant="secondary" onClick={() => shiftMonth(1)}><ChevronRight size={16} /></Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <p className="text-xs uppercase text-slate-500">{t('subscriptions.collected')}</p>
          <p className="mt-1 text-2xl font-bold text-emerald-700">{formatMoney(totals.collected)}</p>
        </Card>
        <Card>
          <p className="text-xs uppercase text-slate-500">{t('subscriptions.outstanding')}</p>
          <p className="mt-1 text-2xl font-bold text-rose-700">{formatMoney(totals.outstanding)}</p>
        </Card>
      </div>

      <Card
        title={t('subscriptions.periodTitle')}
        actions={
          <Button variant="secondary" loading={busyId === 'generate'} onClick={generate}>
            <RefreshCw size={14} />{t('subscriptions.generate')}
          </Button>
        }
      >
        {rows.length === 0 ? (
          <EmptyState
            icon={CreditCard}
            title={t('subscriptions.noPeriods')}
            description={t('subscriptions.noPeriodsHint')}
          />
        ) : (
          <Table headers={[
            t('appointments.professional'), t('specializations.discipline'),
            t('payments.amount'), t('common.status'), t('payments.method'), '',
          ]}>
            {rows.map((row) => (
              <tr key={row.id} className={row.is_blocked ? 'opacity-60' : undefined}>
                <td className="px-3 py-3">
                  <span className="font-medium text-slate-900">{row.first_name} {row.last_name}</span>
                  {row.is_blocked && <Badge tone="danger" className="ml-2">{t('patients.blocked')}</Badge>}
                </td>
                <td className="px-3 py-3 text-sm text-slate-700">
                  {row.discipline ?? <span className="text-amber-700">{t('professionals.noDiscipline')}</span>}
                </td>
                <td className="px-3 py-3 font-medium text-slate-800">
                  {formatMoney(row.amount)}
                  {Number(row.amount) === 0 && (
                    <p className="text-xs font-normal text-amber-700">{t('subscriptions.zeroFeeHint')}</p>
                  )}
                </td>
                <td className="px-3 py-3">
                  {row.status === 'paid' ? (
                    <Badge tone="success"><Check size={12} />{t('payments.status.paid')}</Badge>
                  ) : row.is_overdue ? (
                    <Badge tone="danger"><AlertTriangle size={12} />{t('payments.status.overdue')}</Badge>
                  ) : (
                    <Badge tone="warning">{t('payments.status.pending')}</Badge>
                  )}
                </td>
                <td className="px-3 py-3 text-sm text-slate-600">
                  {row.payment_method ? t(`payments.methods.${row.payment_method}`) : '—'}
                  {row.paid_at && <p className="text-xs text-slate-400">{formatDate(row.paid_at)}</p>}
                </td>
                <td className="px-3 py-3">
                  <div className="flex justify-end gap-1">
                    {row.status === 'paid' && (
                      <Button variant="ghost" loading={busyId === row.id} onClick={() => reopen(row)}
                        title={t('subscriptions.reopen')}>
                        <Undo2 size={14} />
                      </Button>
                    )}
                    {row.status !== 'paid' && (
                      <Button variant="ghost" loading={busyId === row.id}
                        onClick={() => { setPaying(row); setMethod('transfer'); }}
                        title={t('subscriptions.markPaid')}>
                        <Check size={14} />
                      </Button>
                    )}
                    <Button variant="ghost" onClick={() => toggleBlock(row)}
                      title={row.is_blocked ? t('patients.unblock') : t('subscriptions.blockProfessional')}>
                      {row.is_blocked ? <RotateCcw size={14} /> : <Ban size={14} />}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Modal
        open={Boolean(paying)}
        title={t('subscriptions.markPaid')}
        onClose={() => setPaying(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPaying(null)}>{t('common.cancel')}</Button>
            <Button variant="success" onClick={markPaid}>{t('common.confirm')}</Button>
          </>
        }
      >
        {paying && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              {paying.first_name} {paying.last_name} · {formatMoney(paying.amount)}
            </p>
            <Field label={t('payments.method')}>
              <Select value={method} onChange={(event) => setMethod(event.target.value)}>
                {['cash', 'transfer', 'card', 'other'].map((option) => (
                  <option key={option} value={option}>{t(`payments.methods.${option}`)}</option>
                ))}
              </Select>
            </Field>
          </div>
        )}
      </Modal>

      <Modal
        open={showPricing}
        wide
        title={t('subscriptions.feeByDiscipline')}
        onClose={() => setShowPricing(false)}
        footer={<Button onClick={() => setShowPricing(false)}>{t('common.close')}</Button>}
      >
        <p className="mb-4 text-sm text-slate-600">{t('subscriptions.pricingHint')}</p>
        <Table headers={[t('specializations.discipline'), t('subscriptions.monthlyFee'), t('subscriptions.annualFee')]}>
          {plans.map((plan) => (
            <tr key={plan.id}>
              <td className="px-3 py-2 font-medium text-slate-800">{plan.disciplines?.name}</td>
              <td className="px-3 py-2">
                <Input
                  type="number" min="0" className="w-32"
                  defaultValue={plan.monthly_amount}
                  onBlur={(event) => {
                    const value = Number(event.target.value) || 0;
                    if (value !== Number(plan.monthly_amount)) savePricing(plan, { monthly_amount: value });
                  }}
                />
              </td>
              <td className="px-3 py-2">
                <Input
                  type="number" min="0" className="w-32"
                  placeholder={t('subscriptions.annualPlaceholder')}
                  defaultValue={plan.annual_amount ?? ''}
                  onBlur={(event) => {
                    const raw = event.target.value;
                    const value = raw === '' ? null : Number(raw);
                    if (value !== plan.annual_amount) savePricing(plan, { annual_amount: value });
                  }}
                />
              </td>
            </tr>
          ))}
        </Table>
      </Modal>
    </div>
  );
};

export default SubscriptionsView;
